import { useRef, useState, useEffect, useCallback } from 'react';

export interface ContainerDimensions {
  width: number;
  height: number;
  left: number;
  top: number;
}

const EMPTY: ContainerDimensions = { width: 0, height: 0, left: 0, top: 0 };

/**
 * Track an element's dimensions using getBoundingClientRect + ResizeObserver.
 * Unlike offsetLeft/offsetTop, this is always relative to the viewport
 * and immune to changes in the offsetParent chain.
 */
export function useContainerDimensions() {
  const ref = useRef<HTMLElement | null>(null);
  const [dimensions, setDimensions] = useState<ContainerDimensions>(EMPTY);

  const measure = useCallback(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setDimensions({
      width: rect.width,
      height: rect.height,
      left: rect.left + window.scrollX,
      top: rect.top + window.scrollY,
    });
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Initial measurement
    measure();

    // Resize observer for container size changes
    const observer = new ResizeObserver(measure);
    observer.observe(el);

    // Scroll can change the absolute position
    window.addEventListener('scroll', measure, { passive: true });
    // Fullscreen changes dimensions
    document.addEventListener('fullscreenchange', measure);

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', measure);
      document.removeEventListener('fullscreenchange', measure);
    };
  }, [measure]);

  /**
   * Convert a mouse/pointer event to coordinates relative to the container,
   * as percentages (0–100).
   */
  const eventToPercent = useCallback(
    (event: { clientX: number; clientY: number }) => {
      if (!ref.current) return { x: 0, y: 0 };
      const rect = ref.current.getBoundingClientRect();
      return {
        x: ((event.clientX - rect.left) / rect.width) * 100,
        y: ((event.clientY - rect.top) / rect.height) * 100,
      };
    },
    [],
  );

  return { ref, dimensions, measure, eventToPercent };
}
